import { jsx } from 'react/jsx-runtime';
import { forwardRef, useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { HorizontalPositions, SuggestionMenuPositions } from '../types/index.js';
import { useStyles } from '../styles/index.js';
import { useSuggestionMenu } from '../useSuggestionMenu/index.js';
import { useDetectKeyboardOpen } from '../../../hooks/src/useDetectKeyboardOpen/index.js';
import { useCombinedRefs } from '../../../hooks/src/useCombinedHook/index.js';
import { computeResponsiveness } from '../../../../foundation/responsive/helpers/index.js';
import { ControlSizesXL } from '../../../helpers/controlSizes/controlSizes/index.js';

const SuggestionMenu = forwardRef(({ children, className, dataTestId, relativeRef, isOpen = false, size = ControlSizesXL, position = SuggestionMenuPositions.Bottom, alignment = HorizontalPositions.Center, zIndex, onMouseLeave, hasOutsideClick, onClose, maxSpace, onMouseDown, isForSelect, id, portalRef, isDisabled, isForPicker, autoCloseOnScroll, ...props }, ref) => {
    const { contextPosition, setContextHeight, recalculatePosition, } = useSuggestionMenu(relativeRef, isOpen, position);
    const [menuWidth, setMenuWidth] = useState();
    const [isCursorOverMenu, setIsCursorOverMenu] = useState(false);
    const wrapperRef = useRef(null);
    const combineRef = useCombinedRefs(ref, wrapperRef);
    const [open, setOpen] = useState(false);
    const sizes = computeResponsiveness(size);
    const isKeyboardOpen = useDetectKeyboardOpen(relativeRef?.current);
    const styles = useStyles({
        contextPosition, position, sizes, zIndex, alignment, menuWidth, isKeyboardOpen, maxSpace, isDisabled, isForSelect, isForPicker,
    });
    useEffect(() => {
        if (isForSelect)
            return;
        setOpen(isOpen);
    }, [isForSelect, isOpen]);
    useEffect(() => {
        const handleClickOutside = (event) => {
            const selectWrapper = document.getElementById('select-wrapper');
            if (hasOutsideClick && open
                && wrapperRef.current
                && relativeRef.current
                && !wrapperRef.current.contains(event.target)
                && !relativeRef.current.contains(event.target)
                && ((selectWrapper && !selectWrapper.contains(event.target)) || !selectWrapper)) {
                setOpen(false);
                onClose?.(event);
            }
        };
        function handleMouseEnter() {
            setIsCursorOverMenu(true);
        }
        function handleMouseLeave() {
            setIsCursorOverMenu(false);
        }
        function handleOutsideScroll(event) {
            if (isCursorOverMenu) {
                return;
            }
            if (open) {
                setOpen(false);
                onClose?.(event);
            }
        }
        if (open && !isForPicker) {
            window.addEventListener('click', handleClickOutside);
            window.addEventListener('mousedown', handleClickOutside);
            if (autoCloseOnScroll) {
                window.addEventListener('scroll', handleOutsideScroll, true);
            }
            if (wrapperRef.current && autoCloseOnScroll) {
                wrapperRef.current.addEventListener('mouseenter', handleMouseEnter);
                wrapperRef.current.addEventListener('mouseleave', handleMouseLeave);
            }
        }
        return () => {
            window.removeEventListener('click', handleClickOutside);
            !isForPicker && window.removeEventListener('mousedown', handleClickOutside);
            if (autoCloseOnScroll) {
                window.removeEventListener('scroll', handleOutsideScroll, true);
            }
            if (wrapperRef.current && autoCloseOnScroll) {
                wrapperRef.current.removeEventListener('mouseenter', handleMouseEnter);
                // eslint-disable-next-line react-hooks/exhaustive-deps
                wrapperRef.current.removeEventListener('mouseleave', handleMouseLeave);
            }
        };
    }, [open, hasOutsideClick, wrapperRef, relativeRef, onClose, autoCloseOnScroll, isCursorOverMenu, isForPicker]);
    const observerRef = useRef(null);
    const refHandler = useCallback((contextRef) => {
        if (contextRef) {
            const rect = contextRef.getBoundingClientRect();
            setMenuWidth(rect.width);
            setContextHeight(rect.height);
            observerRef.current = new ResizeObserver(entries => {
                for (const entry of entries) {
                    setContextHeight(entry.contentRect.height);
                    recalculatePosition();
                }
            });
            // observerRef.current.observe(contextRef); disabled temporarily to prevent re-renders
            if (document.activeElement === relativeRef?.current && isOpen) {
                contextRef.focus();
            }
            recalculatePosition();
        }
        else {
            observerRef.current?.disconnect();
            observerRef.current = null;
        }
    }, [isOpen, relativeRef, setContextHeight, recalculatePosition]);
    const menu = (jsx("section", { ref: combineRef, onMouseLeave: onMouseLeave, onMouseDown: onMouseDown, id: id, children: jsx("div", { ...props, className: clsx(styles.root, className), "data-testid": dataTestId || 'suggestion-menu', ref: refHandler, children: children }) }));
    return ((isForSelect ? isOpen : open) ? createPortal(menu, portalRef || document.body) : null);
});
SuggestionMenu.displayName = 'SuggestionMenu';

export { SuggestionMenu };
